import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { ActivityLog, FeedbackStatus } from '../../../utility/feedback';
import { RoleplayFeedbackApiService } from '../../../v2/services/roleplay-feedback/roleplay-feedback-api.service';
import { FeedbackCommentTimelineComponent } from '../../feedback/feedback-comment-timeline/feedback-comment-timeline.component';
import { ContactCenterApiService } from '../../../v2/apis/contact-center/contact-center-api.service';
import { RoleplayFeedbackConversationsDialogComponent } from './roleplay-feedback-conversations-dialog/roleplay-feedback-conversations-dialog.component';
import { AssignTicketToDialogComponent } from '../assign-ticket-to-dialog/assign-ticket-to-dialog.component';

@Component({
    selector: 'app-roleplay-feedback-detail',
    templateUrl: './roleplay-feedback-detail.component.html',
    styleUrls: ['./roleplay-feedback-detail.component.scss'],
    standalone: false
})
export class RoleplayFeedbackDetailComponent implements OnInit, OnDestroy {

    @ViewChild('timelineRef') timelineRef!: FeedbackCommentTimelineComponent;

    feedbackId: number = 0;
    feedbackDetail: any = {};
    historyDetails: ActivityLog[] = [];
    statusList: FeedbackStatus[] = [];
    selectedStatus: string = '';
    commentText: string = '';
    isSubmitting: boolean = false;
    isLoading: boolean = false;
    headerData: any = {};
    uploadedFiles: File[] = [];

    private statusSubscription: Subscription | null = null;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private toastr: ToastrService,
        private dialog: MatDialog,
        private roleplayFeedbackApiService: RoleplayFeedbackApiService,
        private contactCenterService: ContactCenterApiService
    ) {
        // Grab row data passed via navigation state to avoid loading twice
        const nav = this.router.getCurrentNavigation();
        if (nav?.extras?.state?.['feedbackRow']) {
            this.feedbackDetail = nav.extras.state['feedbackRow'];
        } else if (window.history.state?.['feedbackRow']) {
            this.feedbackDetail = window.history.state['feedbackRow'];
        }
    }

    ngOnInit(): void {
        this.route.params.subscribe(params => {
            this.feedbackId = +params['id'];
            this.loadFeedbackWithLogs();
        });
        this.loadStatusList();
    }

    ngOnDestroy(): void {
        this.statusSubscription?.unsubscribe();
    }

    loadFeedbackWithLogs(): void {
        this.isLoading = true;
        this.roleplayFeedbackApiService.getRoleplayFeedbackWithLogs(this.feedbackId).subscribe({
            next: (response) => {
                const feedback = response?.feedback || response;
                const activityLogs: any[] = response?.activity_logs || [];

                if (feedback) {
                    this.feedbackDetail = {
                        ...this.feedbackDetail,
                        ...feedback,
                        given_by_name: [feedback.given_by_first_name, feedback.given_by_last_name].filter(Boolean).join(' ') || `User ${feedback.given_by}`
                    };
                }

                this.buildHeaderData();

                // Map activity logs to history (same pattern as request-detail)
                this.historyDetails = activityLogs
                    .map(log => ({
                        id: log.id,
                        request_id: log.feedback_id,
                        wwid: log.given_by,
                        current_status: log.old_status || '',
                        new_status: log.new_status,
                        user_type: log.user_type,
                        assigned_from_category: log.assigned_from_category || log.assigned_from || '',
                        assigned_to_category: log.assigned_to_category || '',
                        message: log.comments || log.description || `${log.activity_type}: Status changed to ${log.new_status}`,
                        created_at: log.created_date || log.created_at,
                        first_name: log.given_by_first_name,
                        last_name: log.given_by_last_name,
                        isUser: log.given_by === Number(sessionStorage.getItem('userId')),
                        attachment: log.uploaded_file || log.attachment || null,
                    }))
                    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

                this.isLoading = false;
            },
            error: () => {
                this.buildHeaderData();
                this.isLoading = false;
            }
        });
    }

    private buildHeaderData(): void {
        const f = this.feedbackDetail;
        this.selectedStatus = f?.feedback_status || 'Open';
        this.headerData = {
            submittedBy: [f?.given_by_first_name, f?.given_by_last_name].filter(Boolean).join(' ') || `User ${f?.given_by}`,
            submittedOn: f?.created_date,
            assigned_category: f?.assigned_category || 'Unassigned',
            status: f?.feedback_status || 'Open',
            rating: f?.rating
        };
    }

    loadStatusList(): void {
        this.statusSubscription = this.contactCenterService.getRequestStatusMetadataList().subscribe({
            next: (response: any) => {
                this.statusList = Array.isArray(response) ? response : (response?.data || []);
            },
            error: () => {
                this.statusList = [];
            }
        });
    }

    onStatusChange(value: string): void {
        this.selectedStatus = value;
    }

    onFilesChanged(files: File[]): void {
        this.uploadedFiles = files;
    }

    addComment(): void {
        if (!this.selectedStatus) {
            this.toastr.error('Please select a status.');
            return;
        }
        if (this.selectedStatus === this.feedbackDetail?.feedback_status && !this.commentText?.trim() && this.uploadedFiles.length === 0) {
            this.toastr.warning('Please change the status or add a comment.');
            return;
        }

        this.isSubmitting = true;
        const activityLogPayload = {
            feedback_id: this.feedbackId,
            activity_type: '',
            description: '',
            old_status: this.feedbackDetail?.feedback_status || '',
            new_status: this.selectedStatus,
            comments: this.commentText?.trim() || `Status changed from ${this.feedbackDetail?.feedback_status || 'Open'} to ${this.selectedStatus}`,
            given_by: Number(sessionStorage.getItem('userId')),
            user_type: 'user',
            business_unit_code: sessionStorage.getItem('businessUnitCode') || ''
        };

        const file = this.uploadedFiles.length > 0 ? this.uploadedFiles[0] : null;

        this.roleplayFeedbackApiService.updateRoleplayFeedbackActivityLog(activityLogPayload, file).subscribe({
            next: () => {
                this.toastr.success('Feedback updated successfully.');
                this.feedbackDetail.feedback_status = this.selectedStatus;
                this.commentText = '';
                this.uploadedFiles = [];
                if (this.timelineRef) this.timelineRef.clearUploadedFiles();
                this.buildHeaderData();
                this.loadFeedbackWithLogs();
                this.isSubmitting = false;
            },
            error: (error) => {
                console.error('Error submitting comment:', error);
                this.toastr.error('Failed to submit comment.');
                this.isSubmitting = false;
            }
        });
    }

    viewFeedbackDetails(): void {
        const sessionId = this.feedbackDetail?.chat_session_id;
        if (!sessionId) {
            this.toastr.warning('No session ID available for this feedback.');
            return;
        }
        this.dialog.open(RoleplayFeedbackConversationsDialogComponent, {
            width: '95%',
            maxWidth: '1400px',
            height: '90vh',
            data: { sessionId, feedbackDetail: this.feedbackDetail },
            panelClass: 'feedback-tabs-dialog'
        });
    }

    openAssignTicketToDialog(): void {
        const dialogRef = this.dialog.open(AssignTicketToDialogComponent, {
            width: '500px',
            data: {
                ticketId: this.feedbackId,
                currentAssignee: this.headerData?.assigned_category || '',
                referenceType: 'roleplay_feedback'
            }
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.headerData = { ...this.headerData, assigned_category: result.assigned_category || 'Unassigned' };
                this.loadFeedbackWithLogs();
            }
        });
    }

    goBack(): void {
        this.router.navigate(['app-cc-roleplay-feedback'], { skipLocationChange: true });
    }
}
